import { Building2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Project, ProjectStatus, ProjectPriority, ProjectLocation } from '../../types';
import { projectService } from '../../services/projectService';
import { DatePicker, Dropdown, Input, TextArea, LocationInput, CurrencyInput } from '../forms';

interface ProjectEditDrawerProps {
  projectId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: Project) => void;
}

interface ProjectFormData {
  name: string;
  projectNumber: string;
  description: string;
  status: ProjectStatus;
  priority: ProjectPriority;
  startDate: string;
  endDate: string;
  budget: number;
  location: ProjectLocation;
}

const statusOptions = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const ProjectEditDrawer: React.FC<ProjectEditDrawerProps> = ({
  projectId,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    projectNumber: '',
    description: '',
    status: 'planning',
    priority: 'medium',
    startDate: '',
    endDate: '',
    budget: 0,
    location: {
      country: '',
      state: '',
      city: '',
      zipCode: '',
      address: '',
    },
  });
  const [originalProject, setOriginalProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load project data when drawer opens
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId || !isOpen) return;

      setIsFetching(true);
      setError(null);

      try {
        const response = await projectService.getProject(projectId);
        if (response.success && response.data) {
          const project = response.data;
          setOriginalProject(project);
          setFormData({
            name: project.name || '',
            projectNumber: project.projectNumber || '',
            description: project.description || '',
            status: project.status || 'planning',
            priority: project.priority || 'medium',
            startDate: project.startDate
              ? new Date(project.startDate).toISOString().split('T')[0]
              : '',
            endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
            budget: project.budget || 0,
            location: project.location || {
              country: '',
              state: '',
              city: '',
              zipCode: '',
              address: project.address || '',
            },
          });
        } else {
          setError(response.message || 'Failed to load project');
        }
      } catch {
        setError('Failed to load project data');
      } finally {
        setIsFetching(false);
      }
    };

    loadProject();
  }, [projectId, isOpen]);

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        projectNumber: '',
        description: '',
        status: 'planning',
        priority: 'medium',
        startDate: '',
        endDate: '',
        budget: 0,
        location: {
          country: '',
          state: '',
          city: '',
          zipCode: '',
          address: '',
        },
      });
      setOriginalProject(null);
      setError(null);
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (location: ProjectLocation) => {
    setFormData(prev => ({ ...prev, location }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      setError('Project name is required');
      return;
    }

    if (!projectId) {
      setError('Project ID is missing');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const updateData = {
        name: formData.name,
        projectNumber: formData.projectNumber,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        startDate: formData.startDate ? new Date(formData.startDate) : undefined,
        endDate: formData.endDate ? new Date(formData.endDate) : undefined,
        budget: formData.budget,
        location: formData.location,
      };

      const response = await projectService.updateProject(projectId, updateData as any);

      if (response.success && response.data) {
        onSave(response.data);
        onClose();
      } else {
        setError(response.message || 'Failed to update project');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 left-6 z-[100] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Building2 className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Edit Project</h2>
                {originalProject && (
                  <p className="text-sm text-gray-500">{originalProject.projectNumber}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {isFetching ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center space-x-3 text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-brand-600"></div>
                  <span>Loading project...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {/* Basic Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Basic Information
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Project Name"
                      value={formData.name}
                      onChange={value => handleInputChange('name', value)}
                      placeholder="Enter project name"
                      required
                    />
                    <Input
                      label="Project Number"
                      value={formData.projectNumber}
                      onChange={value => handleInputChange('projectNumber', value)}
                      placeholder="e.g., PRO-001"
                    />
                  </div>

                  <TextArea
                    label="Description"
                    value={formData.description}
                    onChange={value => handleInputChange('description', value)}
                    placeholder="Describe the project..."
                    rows={3}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Dropdown
                      label="Status"
                      value={formData.status}
                      onChange={value => handleInputChange('status', value)}
                      options={statusOptions}
                    />
                    <Dropdown
                      label="Priority"
                      value={formData.priority}
                      onChange={value => handleInputChange('priority', value)}
                      options={priorityOptions}
                    />
                  </div>
                </div>

                {/* Schedule Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Schedule
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <DatePicker
                      label="Start Date"
                      value={formData.startDate}
                      onChange={value => handleInputChange('startDate', value)}
                    />
                    <DatePicker
                      label="End Date"
                      value={formData.endDate}
                      onChange={value => handleInputChange('endDate', value)}
                    />
                  </div>
                </div>

                {/* Budget Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Budget
                  </h3>

                  <CurrencyInput
                    label="Project Budget"
                    value={formData.budget}
                    onChange={value => handleInputChange('budget', value)}
                  />
                </div>

                {/* Location Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Location
                  </h3>

                  <LocationInput
                    label="Project Address"
                    value={formData.location}
                    onChange={handleLocationChange}
                  />
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || isFetching}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ProjectEditDrawer;
